import { zodFormResolver } from "@/util/zod-form-resolver"
import classNames from "classnames"
import Cookies from "js-cookie"
import * as React from "react"
import { Controller, useForm, useWatch } from "react-hook-form"
import { HiOutlineCube } from "react-icons/hi"
import { IoSwapHorizontalOutline } from "react-icons/io5"
import { z } from "zod"
import type { GoogleAddressData } from "../place-input"

import { shippingCategoryCtaLabels, shippingOptions } from "@/constants/form"
import { useGoogleMaps } from "../../app/hooks/use-google-maps"
import { env } from "../../config"
import { EMAIL_STORAGE } from "../../constants/storage"
import { generateFormQueryParams } from "../../services/links"

import { Email } from "../email"
import { PlaceInput } from "../place-input"
import { Select } from "./select"

const addressSchema = z.object({
  formatted_address: z.string(),
  city: z.string(),
  state_code: z.string(),
  lat: z.number(),
  lng: z.number(),
  zip: z.string().nullable(),
})

const getTransportationSchema = (showCategory: boolean) =>
  z.object({
    pickup: addressSchema.nullable(),
    delivery: addressSchema.nullable(),
    category: showCategory
      ? z
          .string({
            error: "Category is required",
          })
          .min(1, "Category is required")
      : z.string().optional(),
    email: z.string().email("Invalid email").optional().or(z.literal("")),
  })

const getBaseLink = (buttonLink?: string) => {
  if (!buttonLink) {
    return "/shipments/new"
  }

  return buttonLink.startsWith("/") ? buttonLink : `/${buttonLink}`
}

const getSubmitPath = (link: string, category?: string) => {
  const normalizedLink = link.replace(/\/$/, "")

  if (!category) {
    return normalizedLink
  }

  if (normalizedLink === "/shipments/new") {
    return `${normalizedLink}/${category}`
  }

  if (normalizedLink.startsWith("/shipments/new/")) {
    return `/shipments/new/${category}`
  }

  return normalizedLink
}

export type PremiumHeroFormProps = {
  buttonLink?: string
  buttonLabel?: string
  buttonSubLabel?: string
  hasSwapCustomization?: boolean
  geoSessionValue?: string
  hideLabel?: boolean
  font?: string
  customClassName?: string
  hasEmail?: boolean
  showEmail?: boolean
  showCategory?: boolean
  defaultCategory?: string
  categoryAwareButtonLabel?: boolean
  /**
   * Smaller fields and CTA below `md` (unrelated to
   * `PREMIUM_HERO_FORM_COMPACT_LAYOUT`). Pair with `hideLabel` and
   * `PREMIUM_HERO_FORM_ATF_COMPACT_LAYOUT` for the tighter gap.
   */
  compact?: boolean
}

const PREMIUM_HERO_FORM_LAYOUT_BASE = "grid grid-cols-1 items-end min-w-0"

export const PREMIUM_HERO_FORM_DEFAULT_LAYOUT = `${PREMIUM_HERO_FORM_LAYOUT_BASE} gap-y-6 md:grid md:grid-cols-2 md:gap-x-4 lg:flex lg:w-full lg:gap-x-4`

export const PREMIUM_HERO_FORM_ATF_LAYOUT = `${PREMIUM_HERO_FORM_LAYOUT_BASE} gap-4 md:grid md:grid-cols-2 lg:flex lg:w-full lg:gap-x-4 lg:gap-y-0`

export const PREMIUM_HERO_FORM_ATF_COMPACT_LAYOUT = `${PREMIUM_HERO_FORM_LAYOUT_BASE} gap-2 md:grid md:grid-cols-2 md:gap-4 lg:flex lg:w-full lg:gap-x-4 lg:gap-y-0`

export const PREMIUM_HERO_FORM_CATEGORY_LAYOUT = `${PREMIUM_HERO_FORM_LAYOUT_BASE} sm:grid-cols-2 sm:gap-x-4 md:gap-x-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto] lg:gap-x-8`

export const PREMIUM_HERO_FORM_COMPACT_LAYOUT = `${PREMIUM_HERO_FORM_LAYOUT_BASE} gap-y-3 sm:mx-auto sm:max-w-md sm:gap-4 lg:flex lg:space-x-4 lg:max-w-none`

export const PremiumHeroForm: React.FC<PremiumHeroFormProps> = (props) => {
  const {
    buttonLabel,
    buttonLink,
    buttonSubLabel,
    hasSwapCustomization,
    geoSessionValue,
    hideLabel = false,
    hasEmail = true,
    showEmail = true,
    showCategory = false,
    defaultCategory,
    categoryAwareButtonLabel = false,
    compact = false,
    font,
    customClassName = PREMIUM_HERO_FORM_DEFAULT_LAYOUT,
  } = props
  const shouldShowEmail = hasEmail && showEmail
  const transportationSchema = React.useMemo(
    () => getTransportationSchema(showCategory),
    [showCategory],
  )
  const APP_HOST = env("APP_HOST")
  const LINK = getBaseLink(buttonLink)
  const shouldApplyTopMarginOnCta =
    customClassName === PREMIUM_HERO_FORM_CATEGORY_LAYOUT
  const ctaMinHeight = compact ? "min-h-[48px] md:min-h-[56px]" : "min-h-[56px]"
  const getPrefilledPickup = React.useCallback(() => {
    if (!hasSwapCustomization || !geoSessionValue) {
      return null
    }

    try {
      const geoData = JSON.parse(geoSessionValue)

      if (!geoData.country_code2) {
        return null
      }

      return {
        formatted_address: `${geoData.city}, ${geoData.state_prov}, ${geoData.country_name}`,
        city: geoData.city,
        state_code: geoData.state_code,
        lat: parseFloat(geoData.latitude),
        lng: parseFloat(geoData.longitude),
        zip: geoData.zipcode || null,
      } as GoogleAddressData
    } catch (error) {
      console.error("Error parsing geoSessionValue:", error)

      return null
    }
  }, [geoSessionValue, hasSwapCustomization])

  const { handleSubmit, control, getValues, setValue, trigger } = useForm<{
    pickup: null | GoogleAddressData
    delivery: null | GoogleAddressData
    category: string
    email: string
  }>({
    defaultValues: {
      pickup: getPrefilledPickup(),
      delivery: null,
      category: defaultCategory ?? "",
      email: "",
    },
    resolver: zodFormResolver(transportationSchema),
  })

  React.useEffect(() => {
    const prefilledPickup = getPrefilledPickup()

    if (prefilledPickup) {
      setValue("pickup", prefilledPickup, {
        shouldValidate: true,
        shouldDirty: false,
        shouldTouch: false,
      })
    }
  }, [geoSessionValue, setValue, getPrefilledPickup])

  const selectedCategory = useWatch({ control, name: "category" })
  const [hasSelectedCategory, setHasSelectedCategory] = React.useState(false)
  const shouldUseCategoryCtaLabel =
    categoryAwareButtonLabel && hasSelectedCategory
  const ctaLabel = shouldUseCategoryCtaLabel
    ? shippingCategoryCtaLabels[selectedCategory] ?? buttonLabel
    : buttonLabel

  const [shouldLoadMaps, setShouldLoadMaps] = React.useState(false)
  const requestMaps = React.useCallback(() => {
    setShouldLoadMaps(true)
  }, [])
  const { mapsLoaded } = useGoogleMaps({ enabled: shouldLoadMaps })

  const handleSwapLocations = async () => {
    const pickupValue = getValues("pickup")
    const deliveryValue = getValues("delivery")

    setValue("pickup", deliveryValue, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    })
    setValue("delivery", pickupValue, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    })

    await trigger(["pickup", "delivery"])
  }

  const onSubmit = handleSubmit((data) => {
    let queryParams: Record<string, string | number> = {}

    if (data.pickup != null) {
      queryParams.pickupFA = data.pickup.formatted_address
      queryParams.pickupSC = data.pickup.state_code
      queryParams.pickupCt = data.pickup.city
      queryParams.pickupLat = data.pickup.lat
      queryParams.pickupLng = data.pickup.lng
      queryParams.pickupZip = data.pickup.zip ?? ""
    }

    if (data.delivery != null) {
      queryParams.deliveryFA = data.delivery.formatted_address
      queryParams.deliverySC = data.delivery.state_code
      queryParams.deliveryCt = data.delivery.city
      queryParams.deliveryLat = data.delivery.lat
      queryParams.deliveryLng = data.delivery.lng
      queryParams.deliveryZip = data.delivery.zip ?? ""
    }

    if (data.email) {
      queryParams.email = data.email

      try {
        sessionStorage.setItem(EMAIL_STORAGE, data.email)
      } catch {
        // Safari throws when site storage is blocked; the query params still
        // carry the email (the cookie may be blocked too).
      }

      Cookies.set(EMAIL_STORAGE, data.email)
    }

    const formQueryParams = generateFormQueryParams({
      query: queryParams,
    })

    const submitPath = getSubmitPath(LINK, data.category)

    window.location.assign(`${APP_HOST}${submitPath}${formQueryParams}`)
  })

  return (
    <form className={classNames(customClassName)} onSubmit={onSubmit}>
      {showCategory && (
        <div className="sm:flex-1">
          <Controller
            control={control}
            name="category"
            render={({ field, fieldState }) => {
              const error = fieldState.error
                ? fieldState.error.message
                : undefined

              return (
                <div
                  className={
                    hideLabel ? "mt-4 w-full min-w-0" : "w-full min-w-0"
                  }
                >
                  <Select
                    value={field.value || null}
                    ref={field.ref}
                    id="category"
                    onChange={(value) => {
                      field.onChange(value)
                      setHasSelectedCategory(true)
                    }}
                    placeholder={hideLabel ? "What are you shipping?" : ""}
                    label={hideLabel ? undefined : "What are you shipping?"}
                    items={shippingOptions}
                    defaultIcon={
                      <HiOutlineCube size={24} className="w-6 text-gold" />
                    }
                    error={error}
                    isUppercase={false}
                  />
                </div>
              )
            }}
          />
        </div>
      )}
      <div className="sm:flex-1">
        <Controller
          name="pickup"
          control={control}
          render={({ field, fieldState }) => {
            const error = fieldState.error
              ? fieldState.error.message
              : undefined

            return (
              <PlaceInput
                value={field.value}
                onChange={field.onChange}
                placeholder="Pickup location"
                type="pickup"
                error={error}
                id="pickup-location"
                ref={field.ref}
                showLabel={!hideLabel}
                compact={compact}
                mapsLoaded={mapsLoaded}
                onFocus={requestMaps}
              />
            )
          }}
        />
      </div>
      {hasSwapCustomization && (
        <div
          className={classNames(
            "flex w-full justify-center rotate-90 md:rotate-0 md:w-10 h-12 flex-shrink-0 items-center self-center md:mt-4",
            hideLabel ? "mt-2 -mb-2 md:mb-0" : " md:pt-5",
          )}
          data-testid="premium-hero-form"
        >
          <button
            type="button"
            onClick={handleSwapLocations}
            className={classNames(
              "p-2 rounded-full border border-1 border-gold",
            )}
            aria-label="Swap pickup and delivery locations"
          >
            <IoSwapHorizontalOutline className="w-4 md:w-5" />
          </button>
        </div>
      )}
      <div className="sm:flex-1">
        <Controller
          name="delivery"
          control={control}
          render={({ field, fieldState }) => {
            const error = fieldState.error
              ? fieldState.error.message
              : undefined

            return (
              <PlaceInput
                value={field.value}
                onChange={field.onChange}
                placeholder="Delivery location"
                type="dropoff"
                error={error}
                id="dropoff-location"
                showLabel={!hideLabel}
                compact={compact}
                ref={field.ref}
                mapsLoaded={mapsLoaded}
                onFocus={requestMaps}
              />
            )
          }}
        />
      </div>
      {shouldShowEmail && (
        <div className="sm:flex-1 xl:min-w-[232px]">
          <Controller
            control={control}
            name="email"
            render={({ field, fieldState }) => {
              const error = fieldState.error
                ? fieldState.error.message
                : undefined

              return (
                <div>
                  <Email
                    value={field.value}
                    ref={field.ref}
                    onChange={field.onChange}
                    label="Email"
                    placeholder="Email"
                    error={error}
                    id="email"
                    showLabel={!hideLabel}
                    compact={compact}
                  />
                </div>
              )
            }}
          />
        </div>
      )}

      <div>
        <button
          type="submit"
          className={classNames(
            "w-full uppercase text-center bg-blue7 hover:bg-blue-700 text-white xl:min-w-[200px]",
            ctaMinHeight,
            buttonSubLabel
              ? "flex flex-col items-center justify-center leading-tight px-4 py-2"
              : "flex items-center justify-center px-6 py-2 text-base",
            shouldApplyTopMarginOnCta ? "mt-4 lg:mt-0" : "",
            font,
          )}
          onClick={onSubmit}
        >
          {buttonSubLabel ? (
            <>
              <span className="text-base">{ctaLabel}</span>
              <span className="text-[10px] text-balance">{buttonSubLabel}</span>
            </>
          ) : (
            ctaLabel
          )}
        </button>
      </div>
    </form>
  )
}
